import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ModelPickerCombobox } from "@/components/settings/ModelPickerCombobox";
import { JOB_ROWS, type JobKind, type ProviderAxis, type RoutingConfig } from "./aiRoutingTypes";

type Props = {
  config: RoutingConfig;
  set: <K extends keyof RoutingConfig>(key: K, value: RoutingConfig[K]) => void;
  setModelCell: (job: JobKind, axis: ProviderAxis, value: string) => void;
};

export const AiRoutingFormSections = ({ config, set, setModelCell }: Props) => (
  <>
    <div className="flex items-start justify-between gap-4 rounded-lg border bg-muted/30 p-4">
      <div className="space-y-1">
        <Label className="text-base">Master switch — admin pool enabled</Label>
        <p className="text-sm text-muted-foreground">
          When ON, the resolver will try the admin OpenRouter pool for the three
          BYOK-eligible paths (AI Chat, Planning Agent, Writing Agent) when a
          workspace has no BYOK key, subject to the caps below. When OFF,
          behavior is the standard two-rail model (Credits + BYOK).
        </p>
      </div>
      <Switch
        checked={!!config.openrouter_admin_enabled}
        onCheckedChange={(v) => set("openrouter_admin_enabled", v)}
      />
    </div>

    <div className="space-y-3">
      <Label className="text-base">Monthly admin budget — total ($)</Label>
      <p className="text-sm text-muted-foreground">
        Hard ceiling across all admin-pool spend in the calendar month. When exceeded,
        Rail 3 stops routing and the resolver falls through to credits.
      </p>
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        step="0.01"
        value={config.monthly_admin_budget_total_usd ?? 0}
        onChange={(e) => set("monthly_admin_budget_total_usd", Number(e.target.value || 0))}
        className="max-w-xs"
      />
    </div>

    <div className="space-y-3">
      <Label className="text-base">Per-tier monthly caps — per account ($)</Label>
      <p className="text-sm text-muted-foreground">
        Caps are applied per account (sum across the account's workspaces) and reset
        on the 1st of each month.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {(
          [
            ["cap_starter_monthly_usd", "Starter", "cap-starter"],
            ["cap_plus_monthly_usd", "Plus", "cap-plus"],
            ["cap_pro_monthly_usd", "Pro", "cap-pro"],
          ] as const
        ).map(([k, label, id]) => (
          <div key={k} className="space-y-1">
            <Label htmlFor={id} className="text-xs uppercase tracking-wide text-muted-foreground">
              {label}
            </Label>
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={(config[k] as number) ?? 0}
              onChange={(e) => set(k, Number(e.target.value || 0) as RoutingConfig[typeof k])}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Comp / admin / super-admin accounts use the Pro cap as a sane default.
      </p>
    </div>

    <div className="space-y-3">
      <Label className="text-base">Tier emergency disable — admin pool</Label>
      <p className="text-sm text-muted-foreground">
        Blocks the admin pool from serving the BYOK-eligible paths (AI Chat,
        Planning Agent, Writing Agent) on the named tier even when the master
        switch is on. Use only during incidents specific to that tier.
      </p>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4 rounded-md border p-3">
          <Label htmlFor="disable-plus" className="font-normal">Disable Rail 3 for Plus</Label>
          <Switch
            id="disable-plus"
            checked={!!config.disable_premium_writing_for_plus}
            onCheckedChange={(v) => set("disable_premium_writing_for_plus", v)}
          />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-md border p-3">
          <Label htmlFor="disable-pro" className="font-normal">Disable Rail 3 for Pro</Label>
          <Switch
            id="disable-pro"
            checked={!!config.disable_premium_writing_for_pro}
            onCheckedChange={(v) => set("disable_premium_writing_for_pro", v)}
          />
        </div>
      </div>
    </div>

    <div className="space-y-3 border-t pt-4">
      <Label className="text-base">Default models per path</Label>
      <p className="text-sm text-muted-foreground">
        Override the default model used for each of the three AI paths. Pick
        from the frontier-model catalog on either axis, or type any custom
        model ID. Leave a cell blank to use the built-in fallback (shown as
        placeholder). Applies system-wide to every workspace; per-workspace
        BYOK overrides take precedence on the OpenRouter side.
      </p>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="p-2 text-left font-medium">Path</th>
              <th className="p-2 text-left font-medium">Excerptus AI model</th>
              <th className="p-2 text-left font-medium">OpenRouter model</th>
            </tr>
          </thead>
          <tbody>
            {JOB_ROWS.map((row) => {
              const cell = config.model_overrides?.[row.key] ?? {};
              return (
                <tr key={row.key} className="border-t align-top">
                  <td className="p-2 font-medium">
                    <div>{row.label}</div>
                    <div className="text-xs font-normal text-muted-foreground">{row.description}</div>
                  </td>
                  <td className="p-2 min-w-[240px]">
                    <ModelPickerCombobox
                      ariaLabel={`${row.label} — Excerptus AI model`}
                      catalog="lovable"
                      value={(cell.lovable ?? "") as string}
                      placeholder={row.lovableFallback}
                      onChange={(v) => setModelCell(row.key, "lovable", v)}
                    />
                  </td>
                  <td className="p-2 min-w-[240px]">
                    <ModelPickerCombobox
                      ariaLabel={`${row.label} — OpenRouter model`}
                      catalog="openrouter"
                      value={(cell.openrouter ?? "") as string}
                      placeholder={row.openrouterFallback}
                      onChange={(v) => setModelCell(row.key, "openrouter", v)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  </>
);
